/*
  Headline Audio - BSS B2B price fix (JR, 2026-09-03)

  Problem
  -------
  Products the lock app restricts carry the Shopify metafield `seo.hidden = 1`,
  which takes them out of the storefront search index.

  BSS B2B loads the product data it needs to price a page from Shopify search:
      /search.js?q=id:"<product id>"&view=bss.b2b
  For a hidden product that returns [], so BSS B2B never fills its product store,
  never registers the product form, and so never runs the step that clears its own
  anti-flicker CSS:
      [bss-b2b-product-price], [bss-b2b-variant-price] { visibility: hidden }
  Net effect: the Liquid price paints for a moment, then stays invisible forever.

  Fix, in three layers
  --------------------
  1. On a product page, rewrite the app's own data request (its `custom:search/url`
     hook) to /products/{handle}?view=bss.b2b, served by templates/product.bss.b2b.liquid.
     Same JSON shape, same request, so nothing downstream changes and no timing
     changes - this is what actually repairs the page.
  2. Anywhere else (collection cards, quick view), back-fill whatever search could
     not return via the app's `custom:products/search` hook, using /products/{handle}.js.
  3. A last-resort sweep that reveals any price still hidden once BSS B2B has
     finished, so a blank price can never be shown permanently again.

  Loaded from layout/theme.liquid immediately before {{ content_for_header }} so the
  hook object exists before any app script runs.
*/
(function () {
  'use strict';

  // Something later in the page hard-assigns window.bssB2BHooks = {actions,filters},
  // which silently threw away filters registered here. Own the property so any such
  // assignment merges instead of replacing, and our two filters always survive.
  var ours = {};
  var hooks = window.bssB2BHooks || { actions: {}, filters: {} };
  hooks.actions = hooks.actions || {};
  hooks.filters = hooks.filters || {};

  function reassert() {
    Object.keys(ours).forEach(function (name) {
      hooks.filters[name] = ours[name];
    });
  }

  function register(name, fn) {
    ours[name] = fn;
    hooks.filters[name] = fn;
  }

  try {
    Object.defineProperty(window, 'bssB2BHooks', {
      configurable: true,
      get: function () {
        return hooks;
      },
      set: function (next) {
        if (!next || typeof next !== 'object' || next === hooks) return;
        Object.assign(hooks.actions, next.actions || {});
        Object.assign(hooks.filters, next.filters || {});
        reassert();
      }
    });
  } catch (error) {
    // Fallback if the property can't be owned: keep putting them back for a while.
    window.bssB2BHooks = hooks;
    var reassertTimer = window.setInterval(reassert, 250);
    window.setTimeout(function () { window.clearInterval(reassertTimer); }, 20000);
  }

  var CARD_SELECTOR = '[bss-b2b-product-form-id], .card-wrapper, .grid__item, li, form';

  function sameId(a, b) {
    return Number(a) === Number(b);
  }

  // BSS_B2B.shopData.product is only reliable during early init, so read the page's
  // identity from the URL and from the attribute the app itself stamps on the price.
  function pageProductPath() {
    var path = window.location.pathname;
    var at = path.indexOf('/products/');
    if (at === -1) return null;

    var handle = path.slice(at + '/products/'.length).split('/')[0];
    if (!handle) return null;

    return path.slice(0, at) + '/products/' + handle; // keeps any locale prefix
  }

  function pageProductId() {
    var marker = document.querySelector('[bss-b2b-product-id]');
    return marker ? marker.getAttribute('bss-b2b-product-id') : null;
  }

  function idsInSearchUrl(url) {
    var pattern = /id:(?:%22|")(\d+)(?:%22|")/g;
    var ids = [];
    var match;
    while ((match = pattern.exec(String(url))) !== null) ids.push(match[1]);
    return ids;
  }

  // ---------------------------------------------------------------------------
  // 1. Product pages: point the app's data request at this product's own JSON view
  // ---------------------------------------------------------------------------
  register('custom:search/url', function (url) {
    try {
      var base = pageProductPath();
      if (!base) return url;

      // Only when the app is asking for exactly this page's product. Anything
      // broader (collection cards, quick view) must still go through search.
      var ids = idsInSearchUrl(url);
      if (ids.length !== 1) return url;

      var pageId = pageProductId();
      if (!pageId || !sameId(pageId, ids[0])) return url;

      return base + '?view=bss.b2b';
    } catch (error) {
      return url;
    }
  });

  // ---------------------------------------------------------------------------
  // 2. Everywhere else: back-fill products search could not return
  // ---------------------------------------------------------------------------
  function handleFromDom(id) {
    var marker = document.querySelector('[bss-b2b-product-id="' + id + '"]');
    var card = marker && marker.closest ? marker.closest(CARD_SELECTOR) : null;
    var link = card && card.querySelector('a[href*="/products/"]');

    if (link) {
      var tail = (link.getAttribute('href') || '').split('/products/')[1];
      if (tail) return tail.split('?')[0].split('#')[0];
    }

    if (window.location.pathname.indexOf('/products/') > -1) {
      return window.location.pathname.split('/products/')[1].split('?')[0];
    }

    return null;
  }

  function fetchProduct(id) {
    var shopData = window.BSS_B2B && window.BSS_B2B.shopData;
    var product = shopData && shopData.product;

    // The product JSON the theme already rendered beats another round trip, and
    // carries the collection ids that BSS B2B rules can be scoped to.
    if (product && product.id && sameId(product.id, id)) {
      if (!product.collections && shopData.product_collections) {
        product.collections = shopData.product_collections;
      }
      return Promise.resolve(product);
    }

    var handle = handleFromDom(id);
    if (!handle) return Promise.resolve(null);

    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (response) { return response.ok ? response.json() : null; })
      .catch(function () { return null; });
  }

  register('custom:products/search', function (products, context) {
    var found = Array.isArray(products) ? products : [];
    var wanted = (context && context.notExistProductIds) || [];

    var missing = wanted.filter(function (id) {
      return !found.some(function (product) { return sameId(product.id, id); });
    });

    if (!missing.length) return found;

    return Promise.all(missing.map(fetchProduct)).then(function (extra) {
      extra.forEach(function (product) {
        if (!product || !product.id) return;
        if (found.some(function (existing) { return sameId(existing.id, product.id); })) return;
        found.push(product);
      });
      return found;
    });
  });

  // ---------------------------------------------------------------------------
  // 3. Last resort: never leave a price permanently invisible.
  //
  // Held back until BSS B2B says it has finished, so we can't flash an
  // undiscounted price at a customer who is about to be shown a B2B one.
  // Deliberately limited to product prices - a stale cart total is worse than none.
  // ---------------------------------------------------------------------------
  var HIDDEN_PRICES = '[bss-b2b-product-price], [bss-b2b-variant-price]';

  // A lock app may be hiding this price on purpose - BSS B2B's own avoidDetect
  // integration skips price elements carrying these markers for logged-in
  // customers, leaving the real price sitting behind the anti-flicker CSS. Never
  // reveal one of those: it would defeat the lock.
  var LOCK_MARKERS = [
    '.bsscommerce-ltsp-message',
    '.bss-lock-element',
    '.bss-show-lock-messsage',
    '.bss-ca-price-box',
    '[class*="bss-lock-price"]'
  ].join(', ');

  var SETTLE_MS = 500;      // grace period after the app reports it is done
  var FALLBACK_MS = 8000;   // in case that report never arrives
  var SWEEP_EVERY_MS = 1000;
  var GIVE_UP_AFTER_MS = 20000;

  var started = false;

  function sweep() {
    var revealed = 0;

    document.querySelectorAll(HIDDEN_PRICES).forEach(function (element) {
      if (element.style.visibility) return; // BSS B2B has already dealt with it
      if (window.getComputedStyle(element).visibility !== 'hidden') return;
      if (element.querySelector(LOCK_MARKERS)) return; // hidden on purpose
      if (element.closest && element.closest(LOCK_MARKERS)) return;
      element.style.visibility = 'visible';
      revealed += 1;
    });

    if (revealed) {
      console.warn(
        '[Headline] Revealed ' + revealed + ' price element(s) that BSS B2B left hidden. ' +
        'Usually means Shopify search returned no data for the product - check the ' +
        'seo.hidden metafield.'
      );
    }
  }

  function start() {
    if (started) return;
    started = true;

    sweep();
    var timer = window.setInterval(sweep, SWEEP_EVERY_MS);
    window.setTimeout(function () { window.clearInterval(timer); }, GIVE_UP_AFTER_MS);
  }

  window.addEventListener('bss_b2b:module:loaded', function () {
    window.setTimeout(start, SETTLE_MS);
  });
  window.setTimeout(start, FALLBACK_MS);
})();
